import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { schedulingApi } from '../../api/scheduling';
import { queryKeys } from '../queryClient';

export function useScheduleDay(date: string) {
  return useQuery({
    queryKey: queryKeys.schedule(date),
    queryFn: () => schedulingApi.getDay(date),
    staleTime: 30_000,
  });
}

export function useCustomerJobs(customerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.customerJobs(customerId ?? ''),
    queryFn: () => schedulingApi.getCustomerJobs(customerId!),
    enabled: !!customerId,
  });
}

export function useAssignWorker(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, workerId }: { jobId: string; workerId: string }) =>
      schedulingApi.assignWorker(jobId, workerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.schedule(date) }),
  });
}

export function useRescheduleJob(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, newStart, reason }: { jobId: string; newStart: string; reason: string }) =>
      schedulingApi.reschedule(jobId, newStart, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.schedule(date) }),
  });
}

// ── Worker job-application flow ──

export function useOpenJobForApplications(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => schedulingApi.openForApplications(jobId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.schedule(date) }),
  });
}

export function useAvailableJobs(filters?: { service?: string; suburb?: string }) {
  return useQuery({
    queryKey: ['schedule', 'available-jobs', filters?.service ?? '', filters?.suburb ?? ''],
    queryFn: () => schedulingApi.availableJobs(filters),
  });
}

export function useJobApplicants(jobId: string | undefined) {
  return useQuery({
    queryKey: ['schedule', 'applicants', jobId ?? ''],
    queryFn: () => schedulingApi.applicantsFor(jobId!),
    enabled: !!jobId,
  });
}

export function useMyApplications(enabled = true) {
  return useQuery({
    queryKey: ['schedule', 'my-applications'],
    queryFn: () => schedulingApi.myApplications(),
    enabled,
  });
}

export function useApplyToJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => schedulingApi.applyToJob(jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'available-jobs'] });
      qc.invalidateQueries({ queryKey: ['schedule', 'my-applications'] });
    },
  });
}

export function useSelectApplicant(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: string) => schedulingApi.selectApplicant(jobId, applicationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'applicants', jobId] });
      qc.invalidateQueries({ queryKey: ['schedule', 'available-jobs'] });
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'schedule' && q.queryKey[1] !== 'applicants' });
    },
  });
}

export function useDeclineAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => schedulingApi.declineAssignment(jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'available-jobs'] });
      qc.invalidateQueries({ queryKey: ['schedule', 'my-applications'] });
    },
  });
}
